import React, { useState, useMemo, useEffect } from "react";
import { motion } from 'framer-motion';
import { Calendar, Eye, Clock, Code, ArrowRight, X } from 'lucide-react';

const blogPosts = [

  {
    id: 1,
    title: 'Integrando Consulta RUC en tu App: Guía 2025',
    excerpt: 'Implementa validación RUC/DNI en segundos con nuestra API REST. Optimiza tu facturación electrónica y evita multas por datos incorrectos.',
    date: '2025-01-20',
    readTime: '4 min',
    category: 'API Integration',
    image: 'https://images.unsplash.com/photo-1649451844835-7f9d1dc6b0bf?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    content: `La consulta RUC es fundamental para cualquier sistema de facturación electrónica. Con nuestro endpoint especializado, puedes validar contribuyentes en menos de 200ms.

**Características principales:**
- Respuesta en JSON estructurado
- Cache inteligente para consultas recurrentes
- Validación en tiempo real con SUNAT
- Manejo de errores específicos (RUC inactivos, no habidos)

**Ejemplo de implementación:**
\`\`\`javascript
const response = await sunatAPI.ruc.validate('20512345678');
if (response.estado === 'ACTIVO') {
  // Proceder con facturación
}
\`\`\`

Casos de uso reales incluyen e-commerce validando clientes en checkout y sistemas contables automatizando verificaciones.`,
    views: 1500,
    tags: ['RUC', 'Validación', 'API', 'Facturación']
  },
  {
    id: 2,
    title: 'Envío Masivo de Facturas: Mejores Prácticas',
    excerpt: 'Aprende a optimizar el envío de documentos electrónicos en lote. Estrategias para high-throughput y manejo de errores.',
    date: '2025-01-15',
    readTime: '6 min',
    category: 'Best Practices',
    image: 'https://images.unsplash.com/photo-1634836023845-eddbfe9937da?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    content: `El envío masivo de facturas requiere estrategias específicas para garantizar performance y confiabilidad.

**Estrategias recomendadas:**
- Uso de endpoints batch para múltiples documentos
- Implementación de colas de procesamiento
- Manejo robusto de reintentos automáticos
- Monitoreo en tiempo real con webhooks

**Optimización de performance:**
- Chunking de documentos (lotes de 50-100)
- Paralelización controlada de requests
- Cache local de datos recurrentes`,
    views: 890,
    tags: ['Facturación', 'Lotes', 'Performance', 'SOAP']
  }
];

const Blog = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const [selectedPost, setSelectedPost] = useState(null);

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden border-t border-gray-200">
      {/* Efectos de fondo consistentes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0 bg-[linear-gradient(#000_1px,transparent_1px),linear-gradient(90deg,#000_1px,transparent_1px)] bg-[size:64px_64px]" />
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Encabezado */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-50px" }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-500/20 rounded-lg px-4 py-2 mb-6"
          >
            <Code className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-mono text-blue-700">BLOG TÉCNICO</span>
          </motion.div>

          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Insights para{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
              Desarrolladores
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            Tutoriales técnicos, mejores prácticas y actualizaciones sobre integración con APIs SUNAT.
          </p>
        </motion.div>

        {/* Grid de artículos */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {blogPosts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-blue-300 hover:shadow-lg transition-all duration-300 cursor-pointer"
              onClick={() => setSelectedPost(post)}
              whileHover={{ y: -2 }}
            >
              <div className="relative overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                    {post.category}
                  </span>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-3 h-3" />
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Eye className="w-3 h-3" />
                    <span>{post.views}</span>
                  </div>
                </div>

                <h3 className="font-bold text-gray-900 text-lg leading-tight line-clamp-2">
                  {post.title}
                </h3>

                <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                  {post.excerpt}
                </p>

                <div className="flex flex-wrap gap-2">
                  {post.tags.slice(0, 3).map(tag => (
                    <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-2">
                  <span className="text-blue-600 text-sm font-medium group-hover:text-blue-700 transition-colors">
                    Leer artículo completo
                  </span>
                  <ArrowRight className="w-4 h-4 text-blue-600 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* CTA Blog */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-2">¿Buscas más contenido técnico?</h3>
            <p className="text-gray-600 mb-6">Visita nuestro blog completo con tutorials avanzados y casos de estudio</p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium text-sm border border-blue-700 hover:bg-blue-700 transition-colors"
            >
              Ver Todos los Artículos
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Modal de artículo */}
      {selectedPost && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedPost(null)}
        >
          <motion.div
            className="bg-white w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden flex flex-col"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del modal */}
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-gray-900">{selectedPost.title}</h2>
              <button
                onClick={() => setSelectedPost(null)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Contenido del artículo */}
            <div className="p-6 overflow-y-auto flex-1">
              <img
                src={selectedPost.image}
                alt={selectedPost.title}
                className="w-full h-64 object-cover rounded-lg mb-6"
              />

              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
                <div className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>{selectedPost.date}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>{selectedPost.readTime} de lectura</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Eye className="w-4 h-4" />
                  <span>{selectedPost.views} vistas</span>
                </div>
                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                  {selectedPost.category}
                </span>
              </div>

              <div className="prose prose-gray max-w-none">
                {selectedPost.content.split('\n').map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t">
                {selectedPost.tags.map(tag => (
                  <span key={tag} className="bg-gray-100 text-gray-600 text-sm px-3 py-1 rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default Blog;